import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { Controller } from "react-hook-form";

import { FieldDatePicker } from "scmp-app/components/posties/form-drawer/field-date-picker";
import { FieldFileUpload } from "scmp-app/components/posties/form-drawer/field-file-upload";
import type { StudentFormStaticDataType } from "scmp-app/components/posties/form-drawer/types";

import { SchoolField } from "./school-field";
import {
  FormLabel,
  HtmlFormLabel,
  StyledAutocomplete,
  StyledFormControl,
  StyledFormControlLabel,
  StyledFormHelperText,
  StyledTextField,
  WordLimit,
} from "./styles";
import type {
  CustomAutoCompleteOption,
  Question,
  School,
  StudentStaticFields,
  StudentStaticFieldValue,
  StudentStaticResult,
  SubmitFormAnswer,
} from "./types";

export const renderStaticField = (
  itemField: StudentFormStaticDataType & StudentStaticFieldValue,
  key: keyof StudentStaticFields,
  formProps: UseFormReturn<FieldValues>,
): JSX.Element | null => {
  if (!itemField.display) return null;
  const {
    clearErrors,
    control,
    formState: { errors },
    setError,
    setValue,
    watch,
  } = formProps;
  switch (itemField.type) {
    case "autoComplete":
      return (
        <>
          <StyledFormControl fullWidth>
            <FormLabel>{itemField.title}</FormLabel>
            <Controller
              control={control}
              name={key}
              render={({ field }) => {
                const value = field.value as CustomAutoCompleteOption;

                return (
                  <StyledAutocomplete
                    {...field}
                    disablePortal
                    isOptionEqualToValue={(option, value) =>
                      (option as CustomAutoCompleteOption)?.documentId ===
                      (value as CustomAutoCompleteOption).documentId
                    }
                    onChange={(_event, value) => {
                      const selectedValue = value as CustomAutoCompleteOption;
                      field.onChange(selectedValue);
                    }}
                    options={[
                      ...(itemField.options ?? []),
                      ...(itemField?.withOther ? [{ documentId: "other", name: "Other" }] : []),
                    ]}
                    popupIcon={null}
                    renderInput={parameters => (
                      <StyledTextField
                        {...parameters}
                        error={!!errors[key]}
                        fullWidth
                        helperText={String(errors[key]?.message ?? "")}
                        placeholder={itemField.placeholder ?? ""}
                        value={value?.name ?? ""}
                        variant="outlined"
                      />
                    )}
                    {...itemField.autoCompleteProps}
                  />
                );
              }}
            />
          </StyledFormControl>
          {itemField?.others !== undefined &&
            (watch(key) as CustomAutoCompleteOption)?.documentId ===
              itemField?.others?.showOptionDocumentId &&
            renderStaticField(itemField.others.field, itemField.others.fieldKey, formProps)}
        </>
      );
    case "date":
      return (
        <StyledFormControl fullWidth key={key}>
          <Controller
            control={control}
            name={key}
            render={({ field }) => (
              <FieldDatePicker
                errors={errors}
                field={field}
                label={itemField.title ?? ""}
                name={key}
              />
            )}
          />
        </StyledFormControl>
      );
    case "file":
      return (
        <StyledFormControl fullWidth key={key}>
          <FormLabel>{itemField.title}</FormLabel>
          <Controller
            control={control}
            name={key}
            render={({ field }) => (
              <FieldFileUpload
                clearErrors={clearErrors}
                errors={errors}
                field={field}
                name={key}
                placeholder={itemField.placeholder}
                required={itemField.required}
                setError={setError}
                setValue={setValue}
              />
            )}
          />
        </StyledFormControl>
      );
    case "school":
      return <SchoolField formProps={formProps} itemField={itemField} itemKey={key} />;
    case "text":
      return (
        <StyledFormControl fullWidth key={key}>
          <FormLabel>{itemField.title}</FormLabel>
          <Controller
            control={control}
            name={key}
            render={({ field }) => (
              <StyledTextField
                {...field}
                error={!!errors[key]}
                fullWidth
                helperText={String(errors[key]?.message ?? "")}
                onChange={value => {
                  if (itemField.inputProps?.type === "number") {
                    if (value) {
                      field.onChange(Number(field.value));
                    } else {
                      field.onChange(null);
                    }
                  } else {
                    field.onChange(value);
                  }
                }}
                placeholder={itemField.placeholder ?? ""}
                required={itemField.required}
                variant="outlined"
              />
            )}
            rules={{ required: itemField.required }}
          />
        </StyledFormControl>
      );
    default:
      return null;
  }
};

export const renderQuestion = (question: Question, formProps: UseFormReturn<FieldValues>) => {
  const {
    control,
    formState: { errors },
    watch,
  } = formProps;

  const formLabel = question?.question?.html ? (
    <HtmlFormLabel dangerouslySetInnerHTML={{ __html: question.question.html }} />
  ) : null;

  switch (question.answerType) {
    case "multiple_choice":
      const choices = (watch(question.documentId) as string[]) ?? [];
      return (
        <StyledFormControl
          error={!!errors[question.documentId]}
          fullWidth
          key={question.documentId}
        >
          {formLabel}
          <Controller
            control={control}
            name={question.documentId}
            render={({ field }) => (
              <>
                <FormGroup>
                  {question?.options?.map((option, index) => (
                    <StyledFormControlLabel
                      control={
                        <Checkbox
                          checked={choices?.includes(option.value)}
                          disableRipple
                          onChange={event => {
                            const value = choices;
                            const newValue = event.target.checked
                              ? [...value, option.value]
                              : value.filter(item => item !== option.value);
                            const disabled = question?.maxSelect
                              ? newValue.length > Number(question.maxSelect)
                              : false;

                            if (!disabled) {
                              field.onChange(newValue);
                            }
                          }}
                          sx={{
                            "&.Mui-checked": {
                              color: "#0f75bf",
                            },
                            color: "#cccccc",
                          }}
                        />
                      }
                      key={index}
                      label={option.value}
                    />
                  ))}
                </FormGroup>
                {!!errors[question.documentId] && (
                  <StyledFormHelperText>
                    {String(errors[question.documentId]?.message ?? "")}
                  </StyledFormHelperText>
                )}
              </>
            )}
          />
        </StyledFormControl>
      );
    case "radio_button":
      const radioChoice = (watch(question.documentId) as string) ?? "";
      return (
        <StyledFormControl
          error={!!errors[question.documentId]}
          fullWidth
          key={question.documentId}
        >
          {formLabel}
          <Controller
            control={control}
            name={question.documentId}
            render={({ field }) => (
              <>
                <RadioGroup name={question.documentId}>
                  {question?.options?.map((option, index) => (
                    <StyledFormControlLabel
                      control={
                        <Radio
                          checked={radioChoice === option.value}
                          disableRipple
                          onChange={field.onChange}
                          sx={{
                            "&.Mui-checked": {
                              color: "#0f75bf",
                            },
                            color: "#cccccc",
                          }}
                          value={option.value}
                        />
                      }
                      key={index}
                      label={option.value}
                    />
                  ))}
                </RadioGroup>
                {!!errors[question.documentId] && (
                  <StyledFormHelperText>
                    {String(errors[question.documentId]?.message ?? "")}
                  </StyledFormHelperText>
                )}
              </>
            )}
          />
        </StyledFormControl>
      );
    case "textbox":
      const wordLimit = question?.wordLimit ? Number(question.wordLimit) : null;
      const getWordCount = () => {
        if (!wordLimit) return null;
        const currentValue = (watch(question.documentId) as string).trim();
        const valueLength = wordLimit - currentValue.split(" ").length;

        return currentValue ? valueLength : wordLimit;
      };

      return (
        <>
          <StyledFormControl fullWidth key={question.documentId}>
            {formLabel}
            <Controller
              control={control}
              name={question.documentId}
              render={({ field }) => (
                <StyledTextField
                  multiline
                  {...field}
                  error={!!errors[question.documentId]}
                  fullWidth
                  helperText={String(errors[question.documentId]?.message ?? "")}
                  maxRows={5}
                  minRows={3}
                  onChange={event => {
                    field.onChange(event);
                  }}
                  required={question.required}
                  variant="outlined"
                />
              )}
            />
            {wordLimit && <WordLimit>Remaining: {getWordCount()}</WordLimit>}
          </StyledFormControl>
        </>
      );
  }
};

export const getSchool = (school: null | School) => {
  if (typeof school === "object") {
    return school?.documentId === "other" ? "" : school?.documentId;
  }
  return "";
};

export const getAnswer = (formData: StudentStaticResult, questionsIds: string[]) => {
  const answers: SubmitFormAnswer[] = [];
  questionsIds.forEach(id => {
    if (Array.isArray(formData[id])) {
      formData[id].forEach((value: string) => {
        answers.push({ answer: value, questionId: id });
      });
      return;
    } else {
      answers.push({ answer: formData[id] ? String(formData[id]) : "", questionId: id });
    }
  });

  return answers;
};
